import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { useState } from 'react';
import { useAdminUserSession } from '../../../../models/hooks/adminStateHooks';
import { useAdminAccountAddressMutation } from '../../../../queries/Admin/Account/useAdminAccountAddressMutation';
import { invalidateAccountAdminQuery } from '../../../../queries/Admin/Account/useAdminAccountQuery';
import { useAdminAccountWebsiteUrlMutation } from '../../../../queries/Admin/Account/useAdminAccountWebsiteUrlMutation';
import { useUpdateShareFileFolderLinkMutation } from '../../../../queries/Admin/Account/useUpdateShareFileFolderLinkMutation';
import { AdminPermission } from '../../../../viewmodels/AdminViewModels';
import { Button } from '../../../../web/components-v2/Button';
import { Dialog, DialogBody, DialogFooter, DialogPopup, DialogTitle } from '../../../../web/components-v2/Dialog';
import { Input } from '../../../../web/components-v2/Input';
import { XIcon } from '../../../../web/components/svgs/icons/XIcon';
import { accountStructuredFieldsStyles as s } from './accountStructuredFieldsStyles';

/** True only for http(s) URLs — gates the read-mode anchor against a stored `javascript:` URL. */
const isSafeHttpUrl = (url: string | undefined): boolean => /^https?:\/\//i.test((url ?? '').trim());

const useCanModify = (): boolean => useAdminUserSession().hasPermission(AdminPermission.ModifyAccount);

/**
 * An inline-editable single-URL field (Website URL / ShareFile). Edits in place — independent of any
 * card-wide edit mode — and saves through the caller-supplied `onSave` (its own field endpoint).
 * Read-only (`canEdit` false) shows just the value (safe anchor) or a muted "None".
 */
const InlineUrlEditor = ({
	value,
	emptyLabel,
	placeholder,
	canEdit,
	isSaving,
	onSave,
}: {
	value?: string;
	emptyLabel: string;
	placeholder: string;
	canEdit: boolean;
	isSaving: boolean;
	onSave: (value: string) => Promise<unknown>;
}) => {
	const [isEditing, setIsEditing] = useState(false);
	const [draft, setDraft] = useState(value ?? '');

	if (isEditing) {
		const save = async () => {
			await onSave(draft.trim());
			setIsEditing(false);
		};
		return (
			<div className={css(s.editRow)}>
				<Input styles={[s.input]} value={draft} placeholder={placeholder} onChange={e => setDraft(e.target.value)} />
				<Button variant='link' styles={[s.smallLink]} onClick={save} isLoading={isSaving}>
					Save
				</Button>
				<Button
					variant='ghost'
					size='icon'
					styles={[s.cancelButton]}
					aria-label='Cancel edit'
					disabled={isSaving}
					onClick={() => setIsEditing(false)}
				>
					<XIcon width={10} height={10} />
				</Button>
			</div>
		);
	}

	const startEdit = () => {
		setDraft(value ?? '');
		setIsEditing(true);
	};

	if (!value) {
		return canEdit ? (
			<Button variant='link' styles={[s.addLink]} onClick={startEdit}>
				{emptyLabel}
			</Button>
		) : (
			<span className={css(s.value, s.muted)}>None</span>
		);
	}

	return (
		<div className={css(s.actions)}>
			{isSafeHttpUrl(value) ? (
				<a className={css(s.value)} href={value} target='_blank' rel='noopener noreferrer'>
					{value}
				</a>
			) : (
				<span className={css(s.value)}>{value}</span>
			)}
			{canEdit && (
				<Button variant='link' styles={[s.smallLink]} onClick={startEdit}>
					Edit
				</Button>
			)}
		</div>
	);
};

/** Website URL — inline edit, saved via `PUT admin/account/{id}/additionalInfo/websiteUrl`. */
export const AccountWebsiteUrlField = ({ account }: { account: Api.IAccount }) => {
	const canEdit = useCanModify();
	const mutation = useAdminAccountWebsiteUrlMutation();
	return (
		<InlineUrlEditor
			value={account.additionalInfo?.websiteUrl}
			emptyLabel='Add URL'
			placeholder='https://example.com'
			canEdit={canEdit}
			isSaving={mutation.isPending}
			onSave={async websiteUrl => {
				await mutation.mutateAsync({ accountId: account.id, websiteUrl });
				invalidateAccountAdminQuery();
			}}
		/>
	);
};

/** ShareFile Folder — inline edit, saved via `PUT admin/account/{id}/additionalInfo/folderLink`. */
export const AccountShareFileFolderField = ({ account }: { account: Api.IAccount }) => {
	const canEdit = useCanModify();
	const mutation = useUpdateShareFileFolderLinkMutation();
	return (
		<InlineUrlEditor
			value={account.additionalInfo?.shareFileFolderLink}
			emptyLabel='Add Link'
			placeholder='https://…'
			canEdit={canEdit}
			isSaving={mutation.isPending}
			onSave={async folderLink => {
				await mutation.mutateAsync({ accountId: account.id, folderLink });
				invalidateAccountAdminQuery();
			}}
		/>
	);
};

const ADDRESS_FIELDS: { key: keyof Api.IAddress; label: string; required?: boolean }[] = [
	{ key: 'address1', label: 'Address Line 1' },
	{ key: 'address2', label: 'Address Line 2' },
	{ key: 'city', label: 'City', required: true },
	{ key: 'stateProvince', label: 'State / Province', required: true },
	{ key: 'postalCode', label: 'Postal Code' },
	{ key: 'country', label: 'Country' },
];

/** Address — edited in a dialog, saved via `PATCH admin/account/{id}/address` (City + State required). */
export const AccountAddressField = ({ account }: { account: Api.IAccount }) => {
	const canEdit = useCanModify();
	const mutation = useAdminAccountAddressMutation();
	const [isOpen, setIsOpen] = useState(false);
	const [address, setAddress] = useState<Api.IAddress>({});

	const current = account.planDetails?.address;
	const formatted = current
		? [current.address1, current.city, current.stateProvince, current.postalCode].filter(Boolean).join(', ')
		: '';

	const open = () => {
		setAddress({ ...(current ?? {}) });
		setIsOpen(true);
	};
	const canSave = !!address.city?.trim() && !!address.stateProvince?.trim();
	const save = async () => {
		await mutation.mutateAsync({ accountId: account.id, address });
		invalidateAccountAdminQuery();
		setIsOpen(false);
	};

	return (
		<>
			{formatted ? (
				<div className={css(s.actions)}>
					<span className={css(s.value)}>{formatted}</span>
					{canEdit && (
						<Button variant='link' styles={[s.smallLink]} onClick={open}>
							Edit
						</Button>
					)}
				</div>
			) : canEdit ? (
				<Button variant='link' styles={[s.addLink]} onClick={open}>
					Add Address
				</Button>
			) : (
				<span className={css(s.value, s.muted)}>None</span>
			)}

			{isOpen && (
				<Dialog open={isOpen} onOpenChange={next => !next && setIsOpen(false)} dismissOnOutsideClick={false}>
					<DialogPopup styles={[s.popup]}>
						<DialogTitle>Edit Address</DialogTitle>
						<DialogBody styles={[s.dialogBody]}>
							{ADDRESS_FIELDS.map(field => (
								<div key={field.key} className={css(s.addressField)}>
									<span className={css(s.addressLabel)}>{field.required ? `${field.label} *` : field.label}</span>
									<Input
										value={address[field.key] ?? ''}
										onChange={e => setAddress(prev => ({ ...prev, [field.key]: e.target.value }))}
									/>
								</div>
							))}
						</DialogBody>
						<DialogFooter>
							<Button variant='secondary' onClick={() => setIsOpen(false)} disabled={mutation.isPending}>
								Cancel
							</Button>
							<Button onClick={save} isLoading={mutation.isPending} disabled={!canSave}>
								Save
							</Button>
						</DialogFooter>
					</DialogPopup>
				</Dialog>
			)}
		</>
	);
};
